import React, { useState, useMemo } from 'react';
import { Employee } from '../types';
import { Users, Filter, Check, ChevronDown } from 'lucide-react';

export const EMPLOYEE_CATEGORIES = [
  'Technician',
  'Engineer',
  'Manager',
  'Customer Care',
  'Accountant',
  'Administrative',
  'Driver',
  'Cleaner',
  'Security',
  'Other'
];

interface EmployeeCategorySelectorProps {
  employees: Employee[];
  selectedId?: string;
  selectedIds?: string[];
  onChangeSingle?: (employeeId: string, employeeName: string) => void;
  onChangeMulti?: (employeeIds: string[], employeeNames: string) => void;
  isMulti?: boolean;
  placeholder?: string;
  defaultCategory?: string;
  className?: string;
}

export const getEmployeeCategory = (emp: Employee): string => {
  if (emp.category && emp.category.trim()) {
    return emp.category.trim();
  }
  // Fallback detection based on position/department
  const pos = (emp.position || '').toLowerCase();
  const dept = (emp.department || '').toLowerCase();
  
  if (pos.includes('tech') || dept.includes('tech')) return 'Technician';
  if (pos.includes('eng') || dept.includes('eng')) return 'Engineer';
  if (pos.includes('manag') || dept.includes('manag')) return 'Manager';
  if (pos.includes('cust') || dept.includes('care') || dept.includes('cust')) return 'Customer Care';
  if (pos.includes('account') || dept.includes('account') || pos.includes('finan')) return 'Accountant';
  if (pos.includes('admin') || dept.includes('admin')) return 'Administrative';
  if (pos.includes('driv')) return 'Driver';
  if (pos.includes('clean')) return 'Cleaner';
  if (pos.includes('secur')) return 'Security';
  
  return 'Other';
};

export const EmployeeCategorySelector: React.FC<EmployeeCategorySelectorProps> = ({
  employees,
  selectedId,
  selectedIds = [],
  onChangeSingle,
  onChangeMulti,
  isMulti = false,
  placeholder = 'Select Employee...',
  defaultCategory = 'Technician',
  className = ''
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Extract all categories dynamically including custom ones
  const categoriesList = useMemo(() => {
    const set = new Set(EMPLOYEE_CATEGORIES);
    employees.forEach(emp => {
      const cat = getEmployeeCategory(emp);
      if (cat) set.add(cat);
    });
    return Array.from(set);
  }, [employees]);

  // Filter employees by selected category and search term
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const cat = getEmployeeCategory(emp);
      const matchesCat = selectedCategory === 'ALL' || cat.toLowerCase() === selectedCategory.toLowerCase();
      const name = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase();
      const matchesSearch = !searchTerm || name.includes(searchTerm.toLowerCase()) || (emp.department || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [employees, selectedCategory, searchTerm]);

  const handleSelectSingle = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val) {
      if (onChangeSingle) onChangeSingle('', '');
      return;
    }
    const emp = employees.find(item => item.id === val);
    if (emp && onChangeSingle) {
      onChangeSingle(emp.id, `${emp.firstName || ''} ${emp.lastName || ''}`.trim());
    }
  };

  const handleToggleMulti = (emp: Employee) => {
    if (!onChangeMulti) return;
    const current = [...selectedIds];
    const index = current.indexOf(emp.id);
    let updatedIds: string[];
    if (index >= 0) {
      updatedIds = current.filter(id => id !== emp.id);
    } else {
      updatedIds = [...current, emp.id];
    }

    const selectedEmpObjects = employees.filter(e => updatedIds.includes(e.id));
    const names = selectedEmpObjects
      .map(e => `${e.firstName || ''} ${e.lastName || ''}`.trim().toUpperCase())
      .join('\n'); // Stacked or comma separated

    onChangeMulti(updatedIds, names);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Category selector row */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-[10px] font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">
          <Filter className="w-3 h-3 text-blue-600" />
          <span>Category:</span>
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-xl text-xs font-bold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-800/20"
        >
          <option value="ALL">All Categories</option>
          {categoriesList.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Employee List / Dropdown */}
      {!isMulti ? (
        <select
          value={selectedId || ''}
          onChange={handleSelectSingle}
          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
        >
          <option value="">-- {placeholder} ({filteredEmployees.length}) --</option>
          {filteredEmployees.map(emp => (
            <option key={emp.id} value={emp.id}>
              {emp.firstName} {emp.lastName} ({emp.position || emp.department || getEmployeeCategory(emp)})
            </option>
          ))}
        </select>
      ) : (
        <div className="border border-slate-200 rounded-xl p-2 bg-slate-50/50 max-h-40 overflow-y-auto space-y-1">
          <input
            type="text"
            placeholder="Search staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold mb-1 focus:outline-none"
          />
          {filteredEmployees.length > 0 ? (
            filteredEmployees.map(emp => {
              const isChecked = selectedIds.includes(emp.id);
              return (
                <label
                  key={emp.id}
                  onClick={() => handleToggleMulti(emp)}
                  className={`flex items-center justify-between p-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors ${
                    isChecked ? 'bg-blue-100 text-blue-900 border border-blue-300' : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  <span className="truncate">{emp.firstName} {emp.lastName} <span className="text-[10px] text-slate-400 font-normal">({emp.position || getEmployeeCategory(emp)})</span></span>
                  <div className={`w-4 h-4 rounded flex items-center justify-center border ${isChecked ? 'bg-blue-900 border-blue-900 text-white' : 'border-slate-300 bg-white'}`}>
                    {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                  </div>
                </label>
              );
            })
          ) : (
            <p className="text-[10px] text-slate-400 italic text-center py-2">No staff found in this category</p>
          )}
        </div>
      )}
    </div>
  );
};
